'use client'

import clsx from 'clsx'
import React, { useCallback } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { Control, FieldValues, Path, PathValue, useFormContext } from 'react-hook-form'

import { InputProps } from '@/components/form/input/Input'
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/form/Form'
import { Stack } from '@/components/layout/stack'
import { input } from '@/components/form/input/Input.css'
import { debounce } from '@/utils/debounce'

type Props<T> = {
	name: Path<T>
	label?: string
	description?: string
	route?: string
	withUrlParams?: boolean
	disabled?: boolean
} & InputProps

export const FormInput = <T extends FieldValues>({
	label,
	name,
	type = 'text',
	withUrlParams,
	disabled,
	...props
}: Props<T>) => {
	const pathname = usePathname()
	const searchParams = useSearchParams()
	const params = new URLSearchParams(searchParams)
	const router = useRouter()
	const form = useFormContext<T>()

	// const { onChange } = form.register(name)

	const buildQueryString = useCallback(
		(name: string, value: string) => {
			params.set(name, value)
			return params.toString()
		},
		[searchParams]
	)

	const debouncedRouteUpdate = useCallback(
		debounce((...args: unknown[]) => {
			const value = args[0] as string
			if (value === '') {
				params.delete(name)
				router.push(pathname + '?' + params.toString())
			} else {
				router.push(pathname + '?' + buildQueryString(name, value))
			}
		}, 300),
		[buildQueryString, router]
	)

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		form.setValue(name as Path<T>, e.target.value as PathValue<T, Path<T>>)
		if (withUrlParams) {
			debouncedRouteUpdate(e.target.value)
		}
	}

	const { size, ...restProps } = props

	return (
		<FormField
			control={form.control as Control}
			name={name}
			render={({ field }) => (
				<FormItem>
					{label && <FormLabel>{label}</FormLabel>}
					<Stack gap={1}>
						<FormControl>
							<input
								{...field}
								type={type}
								disabled={disabled}
								onChange={event => {
									handleChange(event)
								}}
								className={clsx(input({ variant: 'default', size: 'default', shadow: 'none' }))}
								{...restProps}
							/>
						</FormControl>
						{props.description && <FormDescription />}
						<FormMessage />
					</Stack>
				</FormItem>
			)}
		/>
	)
}
